import fs from 'node:fs';
import path from 'node:path';
import { HEADER_COMMENT } from './header-format';

export function tweakImports(source: string): string {
  const importRegex = /import \{([^}]*)\} from "drizzle-orm\/pg-core";?/;
  const match = source.match(importRegex);
  if (!match) {
    return source;
  }

  const identifiers = match[1]
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .filter((id) => id !== 'pgSchema' && id !== 'customType');

  // Remove timestamp if it's no longer used in the source
  // (after timestamp -> customTimestamptz replacement)
  const filteredIdentifiers = identifiers.filter((id) => {
    if (id === 'timestamp') {
      // Check if timestamp() is still used anywhere
      const timestampUsageRegex = /timestamp\s*\(/;
      return timestampUsageRegex.test(source);
    }
    return true;
  });

  if (source.includes('pgTable(') && !filteredIdentifiers.includes('pgTable')) {
    filteredIdentifiers.push('pgTable');
  }
  if (source.includes('pgView(') && !filteredIdentifiers.includes('pgView')) {
    filteredIdentifiers.push('pgView');
  }
  if (source.includes('pgMaterializedView(') && !filteredIdentifiers.includes('pgMaterializedView')) {
    filteredIdentifiers.push('pgMaterializedView');
  }
  if (source.includes('pgEnum(') && !filteredIdentifiers.includes('pgEnum')) {
    filteredIdentifiers.push('pgEnum');
  }
  if (source.includes('pgSequence(') && !filteredIdentifiers.includes('pgSequence')) {
    filteredIdentifiers.push('pgSequence');
  }

  const unique = Array.from(new Set(filteredIdentifiers));
  const replacement = `import { ${unique.join(', ')} } from "drizzle-orm/pg-core"`;
  return source.replace(importRegex, replacement);
}

export function ensureTypesImport(source: string): string {
  if (source.includes('from "./types"')) {
    return source;
  }

  const typesImport = 'import { userProfile, fileAttachment } from "./types"\n';
  const headerPrefix = `${HEADER_COMMENT}\n`;
  if (source.startsWith(headerPrefix)) {
    const insertionPoint = headerPrefix.length;
    return `${source.slice(0, insertionPoint)}${typesImport}${source.slice(insertionPoint)}`;
  }

  const importSectionMatch = source.match(/^(?:import [^\n]+\n)+/);
  if (importSectionMatch) {
    const insertionPoint = importSectionMatch[0].length;
    return `${source.slice(0, insertionPoint)}${typesImport}${source.slice(insertionPoint)}`;
  }

  return `${typesImport}${source}`;
}

export function removeInlineTypeHelpers(source: string): string {
  return source
    .replace(/type UserProfile = [\s\S]*?\}\);\n*/g, '')
    .replace(/type FileAttachment = [\s\S]*?\}\);\n*/g, '');
}

export function inlineCustomTypes(source: string): string {
  // Remove any existing import from "./types"
  let text = source.replace(/import \{[^}]*\} from ["']\.\/types["'];?\n*/g, '');

  // Try multiple possible paths for the template file
  const possiblePaths = [
    // When bundled by tsup - __dirname points to dist/
    path.resolve(__dirname, 'template', 'types.ts'),
    // When running from source (development) - relative to helper/imports.ts
    path.resolve(__dirname, '../template', 'types.ts'),
    // Alternative paths
    path.resolve(__dirname, '../../template', 'types.ts'),
    path.resolve(__dirname, '../../../template', 'types.ts'),
  ];

  let templatePath: string | undefined;
  for (const possiblePath of possiblePaths) {
    if (fs.existsSync(possiblePath)) {
      templatePath = possiblePath;
      break;
    }
  }

  if (!templatePath) {
    console.warn('[postprocess-drizzle-schema] Template types file not found. Tried paths:', possiblePaths);
    return text;
  }

  return inlineFromTemplate(text, templatePath);
}

export function inlineFromTemplate(text: string, templatePath: string): string {
  const templateContent = fs.readFileSync(templatePath, 'utf8');

  // Extract the type definitions (everything except the imports)
  const typeDefinitions = templateContent
    .replace(/^import\s+.*;\r?\n*/gm, '') // Remove all import statements
    .trim();

  // Check if we need to add imports for sql and customType
  const needsSql = typeDefinitions.includes('sql`') && !text.includes("from 'drizzle-orm'") && !text.includes('from "drizzle-orm"');
  const needsCustomType = typeDefinitions.includes('customType<') && !text.includes('customType');

  // Add necessary imports to drizzle-orm/pg-core import
  if (needsCustomType) {
    text = ensureImportIdentifier(text, 'drizzle-orm/pg-core', 'customType');
  }

  // Add sql import from drizzle-orm if needed
  if (needsSql && !text.includes("from 'drizzle-orm'") && !text.includes('from "drizzle-orm"')) {
    const importMatch = text.match(/^import [\s\S]*?from ["']drizzle-orm\/pg-core["'];?\n/m);
    if (importMatch) {
      const insertPoint = text.indexOf(importMatch[0]) + importMatch[0].length;
      text = text.slice(0, insertPoint) + "import { sql } from 'drizzle-orm';\n" + text.slice(insertPoint);
    }
  }

  // Find insertion point: after the imports section
  const headerPrefix = `${HEADER_COMMENT}\n`;
  let insertionPoint = 0;

  if (text.startsWith(headerPrefix)) {
    insertionPoint = headerPrefix.length;
  }

  const importSectionMatch = text.slice(insertionPoint).match(/^(?:import [^\n]+\n)+/);
  if (importSectionMatch) {
    insertionPoint += importSectionMatch[0].length;
  }

  // Insert type definitions
  const typeBlock = `\n${typeDefinitions}\n\n`;
  return text.slice(0, insertionPoint) + typeBlock + text.slice(insertionPoint);
}

export function ensureImportIdentifier(source: string, packageName: string, identifier: string): string {
  const escapedPackage = packageName.replace(/\//g, '\\/');
  const importRegex = new RegExp(`import \\{([^}]*)\\} from ["']${escapedPackage}["'];?`);
  const match = source.match(importRegex);

  if (!match) {
    // No import from this package, can't add it
    return source;
  }

  const identifiers = match[1]
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (identifiers.includes(identifier)) {
    return source; // Already imported
  }

  identifiers.push(identifier);
  const unique = Array.from(new Set(identifiers));
  const replacement = `import { ${unique.join(', ')} } from "${packageName}"`;
  return source.replace(importRegex, replacement);
}
